
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Web3Forms API integration
      const formData = new FormData();
      formData.append('access_key', import.meta.env.VITE_WEB3FORMS_API_KEY);
      formData.append('name', name);
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      formData.append('to', 'contact@talento-ai.com');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Message sent successfully! We\'ll be in touch soon.');
        
        // Reset form
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center mb-8 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
            Contact Us
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <Card className="border border-[#0EA5E9]/20 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-[#0EA5E9]">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input 
                      id="name" 
                      placeholder="Your name" 
                      value={name} 
                      onChange={(e) => setName(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="your.email@example.com" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Select 
                      value={subject} 
                      onValueChange={setSubject}
                      required
                    >
                      <SelectTrigger id="subject" className="w-full">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AI Agent Development">AI Agent Development</SelectItem>
                        <SelectItem value="SOP Automation">SOP Automation</SelectItem>
                        <SelectItem value="Talent Recruitment">Talent Recruitment</SelectItem>
                        <SelectItem value="AI Training">AI Training</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      placeholder="How can we help you?" 
                      rows={5} 
                      value={message} 
                      onChange={(e) => setMessage(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-[#0EA5E9] to-[#3B82F6] hover:from-[#0EA5E9]/90 hover:to-[#3B82F6]/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            <div className="space-y-6">
              <Card className="border border-[#0EA5E9]/20 shadow-md">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-4 text-[#0EA5E9]">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Mail className="w-5 h-5 text-[#0EA5E9] mt-1 mr-3" />
                      <div>
                        <p className="font-medium">Email</p>
                        <a href="mailto:info@talento-ai.com" className="text-[#0EA5E9] hover:underline">
                          contact@talento-ai.com
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Phone className="w-5 h-5 text-[#0EA5E9] mt-1 mr-3" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <a href="tel:+18176164295" className="text-[#0EA5E9] hover:underline">
                          +1(817)-616-4295
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#0EA5E9] mt-1 mr-3" />
                      <div>
                        <p className="font-medium">Address</p>
                        <p>
                           304 S. Jones Blvd,<br />
                           Las Vegas, NV, 89107
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border border-[#0EA5E9]/20 shadow-md">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-4 text-[#0EA5E9]">Office Hours</h3>
                  <p className="mb-2"><span className="font-medium">Sunday - Thursday:</span> 7:00 PM - 5:00 AM PST</p>
                  <p className="mb-2"><span className="font-medium">Friday:</span> 7:00 PM - 11:59 PM PST</p>
                  <p><span className="font-medium">Saturday:</span> Closed</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;
